import React, { useState } from 'react';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useDispatch } from 'react-redux';
import { useLocation, Link} from 'react-router-dom';
import { destroyAuthSession } from '../../../redux/slices/session.slice';
import { Logo } from 'assets/images';
import { routes } from 'utils/routes';
import './style.scss';

// Define the type for a menu item
interface MenuItem {
  label: string;
  link: string;
  icon?: string;
  children?: MenuItem[];
}

const Sidebar = () => {
  const dispatch = useDispatch();
  const location = useLocation();


  const [isLogoutDialogVisible, setIsLogoutDialogVisible] = useState(false);
  const [activeParent, setActiveParent] = useState<number | null>(null);

  const confirmLogout = () => {
    dispatch(destroyAuthSession());
    setIsLogoutDialogVisible(false);
  };

  const handleLogoutClick = () => {
    setIsLogoutDialogVisible(true);
  };

  const footerItems = [
    { label: 'Sign out', icon: 'pi pi-fw pi-sign-out', command: handleLogoutClick },
  ];

  const menuItems = [
    {
      label: "Promocodes",
      icon: "pi pi-tags",
      link: routes.promocode,
    },
    {
      label: "Employees",
      icon: "pi pi-user",
      link: routes.employees,
    },
    {
      label: "Customers",
      icon: "pi pi-users",
      children: [
        { label: "Online Customers", link: `${routes.customers}?type=online`, className: "active" },
        { label: "Offline Customers", link: `${routes.customers}?type=offline` },
      ],
    },
    {
      label: "Stores",
      icon: "pi pi-shop",
      children: [
        { label: "Local Stores", link: `${routes.stores}?type=local` },
        { label: "Global Stores", link: `${routes.stores}?type=global` },
        { label: "Reviews & Ratings", link: `${routes.review}` },
      ],
    },
    {
      label: "Deals",
      icon: "pi pi-user",
      link: routes.deals,
    },
    {
      label: "Banners",
      icon: "pi pi-id-card",
      children: [
        { label: "Local Banners", link: `${routes.banners}?type=local` },
        { label: "Global Banners", link: `${routes.banners}?type=global` },
      ],
    },
    {
      label: "Notifications",
      icon: "pi pi-cog",
      link: routes.notification,
    },
    {
      label: "Quicklinks",
      icon: "pi pi-link",
      children: [
        { label: "Local Quicklinks", link: `${routes.quicklinks}?type=local` },
        { label: "Global Quicklinks", link: `${routes.quicklinks}?type=global` },
      ],
    },
    {
      label: "Services",
      icon: "pi pi-cog",
      link: routes.services,
    },
    {
      label: "Blogs",
      icon: "pi pi-book",
      link: routes.blogs,
    },
    {
      label: "Reports",
      icon: "pi pi-chart-bar",
      children: [
        { label: "Payment Reports", link: routes.reports },
        { label: "Service Reports", link: routes.reports },
        { label: "Promocode Reports", link: routes.reports },
        { label: "Referral Reports", link: routes.reports },
        { label: "Employee Customer Earning Reports", link: routes.reports },
        { label: "Username Earning Reports", link: routes.reports },
        { label: "Shopkeeper Earning Reports", link: routes.reports },
      ],
    },
    {
      label: "Profile",
      icon: "pi pi-user-edit",
      link: routes.profile,
    },
  ];
  // Function to check if the current item is active
  const isActive = (link: string): boolean =>
    location.pathname + location.search === link;

  // // Function to check if the current item is active
  // const isActive = (link: string): boolean => {
  //   const [linkPath, linkQuery] = link.split("?");
  //   const [currentPath, currentQuery] = location.pathname.split("?");

  //   // Match the path
  //   const isPathMatching = location.pathname.startsWith(linkPath);

  //   // Match the query parameters (optional)
  //   const isQueryMatching = !linkQuery || location.search.includes(linkQuery);

  //   return isPathMatching && isQueryMatching;
  // };
  // Function to check if any child is active
  const hasActiveChild = (children: MenuItem[] | undefined): boolean =>
    !!children && children.some((child) => isActive(child.link));

  // Handle parent click
  const handleParentClick = (index: number) => {
    setActiveParent((prev) => (prev === index ? null : index));
  };
  return (
    <div className="main-sidebar bg-white flex flex-column h-full">
      <div className='sidebar-logo flex justify-content-center align-items-center border-bottom-1 border-primary-color'>
        <Link to='/'><img src={Logo} alt="Logo" className='flex w-14rem' /></Link>
      </div>

      <div className='menu-items-list overflow-y-auto'>
        <ul>
          {menuItems.map((item, index) => (
            <li
              key={index}
              className={
                isActive(item.link || "") ||
                  (hasActiveChild(item.children) ||
                    activeParent === index)
                  ? "active"
                  : ""
              }
            >
              {item.children ? (
                <div className="side-accordion">
                  <input
                    className="side-accordion-trigger"
                    id={`side-accordion-${index}`}
                    type="checkbox"
                    defaultChecked={hasActiveChild(item.children)}
                  />
                  <label
                    className="side-accordion-title"
                    htmlFor={`side-accordion-${index}`}
                    onClick={() => handleParentClick(index)}
                  >
                    <i className={item.icon}></i>
                    {item.label}
                  </label>
                  <div className="side-accordion-content-wrapper">
                    <div className="side-accordion-content">
                      <ul>
                        {item.children.map((child, childIndex) => (
                          <li key={childIndex}>
                            <Link
                              to={child.link}
                              className={isActive(child.link) ? "active" : ""}
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={item.link}
                  onClick={() => handleParentClick(index)}
                >
                  <i className={item.icon}></i>
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className='menu-items-list footer-menu mt-auto border-top-1 border-primary-color'>
        <Menu model={footerItems} className='border-none w-full' />
      </div>

      <Dialog
        header="Confirm Logout"
        visible={isLogoutDialogVisible}
        style={{ width: '400px' }}
        onHide={() => setIsLogoutDialogVisible(false)}
        footer={
          <>
            <div className='flex justify-content-center	gap-3'>
              <Button label="No" icon="pi pi-times" onClick={() => setIsLogoutDialogVisible(false)} className="m-0" severity="danger" outlined />
              <Button label="Yes" icon="pi pi-check" onClick={confirmLogout} autoFocus className="primary m-0" />
            </div>
          </>
        }
      >
        <p>Are you sure you want to log out?</p>
      </Dialog>

    </div>
  );
};


export default Sidebar;
